/**
 * Listings Service
 * Handles all listing-related API calls
 */

import network from '../../Network/NetworkManager';

const APPURL = require('../../Constants/URLConstants');

/**
 * Create a new listing
 * @param {object} listingData - Listing data object
 * @returns {Promise<object>} - Response with listing object
 */
export const createListing = async (listingData) => {
  try {
    const response = await network.networkCall(
      'v1/listings',
      'POST',
      JSON.stringify({ listing: listingData }),
    );

    return response;
  } catch (error) {
    console.error('Error creating listing:', error);
    throw error;
  }
};

/**
 * Update an existing listing
 * @param {number} listingId - Listing ID
 * @param {object} listingData - Updated listing data
 * @returns {Promise<object>} - Response with updated listing
 */
export const updateListing = async (listingId, listingData) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}`,
      'PATCH',
      JSON.stringify({ listing: listingData }),
    );

    return response;
  } catch (error) {
    console.error('Error updating listing:', error);
    throw error;
  }
};

/**
 * Get listing by ID
 * @param {number} listingId - Listing ID
 * @returns {Promise<object>} - Response with listing data
 */
export const getListingById = async (listingId) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}`,
      'GET',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching listing:', error);
    throw error;
  }
};

/**
 * Get listing by slug
 * @param {string} slug - Listing slug
 * @returns {Promise<object>} - Response with listing data
 */
export const getListingBySlug = async (slug) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.products}/v1/listings/by_slug/${slug}`,
      'GET',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching listing by slug:', error);
    throw error;
  }
};

/**
 * Delete a listing
 * @param {number} listingId - Listing ID
 * @returns {Promise<object>} - Response
 */
export const deleteListing = async (listingId) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.products}/v1/listings/${listingId}`,
      'DELETE',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error deleting listing:', error);
    throw error;
  }
};

/**
 * Get all listings with pagination
 * @param {object} params - Query parameters
 * @returns {Promise<object>} - Response with listings array
 */
export const getListings = async (params = {}) => {
  try {
    const { page = 1, perPage = 20, ...otherParams } = params;

    const queryString = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...otherParams,
    }).toString();

    const response = await network.networkCall(
      `${APPURL.URLPaths.products}/v1/listings?${queryString}`,
      'GET',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching listings:', error);
    throw error;
  }
};

/**
 * Like/unlike listing
 * @param {number} listingId - Listing ID
 * @param {boolean} like - True to like, false to unlike
 * @returns {Promise<object>} - Response
 */
export const toggleListingLike = async (listingId, like = true) => {
  try {
    const endpoint = like ? 'likes' : 'unlikes';

    const response = await network.networkCall(
      `${APPURL.URLPaths.products}/v1/listings/${listingId}/${endpoint}`,
      'POST',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error toggling like:', error);
    throw error;
  }
};
